import type { Metadata } from "next"
import Link from "next/link"
import {
  ArrowRight,
  ChefHat,
  Package,
  Scale,
  ShoppingBasket,
  Calculator,
} from "lucide-react"

import { Container } from "@/components/layout/container"
import { KuhlykTip } from "@/components/kuhlyk/kyhlyk-tip"
import { siteConfig } from "@/data/site"
import { absoluteUrl } from "@/lib/utils"

export const metadata: Metadata = {
  title: "Товари для домашніх заготовок",
  description:
    "Корисні товари для домашніх заготовок: банки, кришки, ваги, кухонні інструменти та практичне приладдя для консервації, ферментації й сезонної кухні.",
  alternates: {
    canonical: "/products",
  },
  openGraph: {
    title: "Товари для домашніх заготовок",
    description:
      "Банки, кришки, ваги, кухонні інструменти та корисне приладдя для домашніх заготовок.",
    url: absoluteUrl("/products", siteConfig.url),
    siteName: siteConfig.name,
    type: "website",
    images: [
      {
        url: absoluteUrl(siteConfig.ogImage, siteConfig.url),
        width: 1200,
        height: 630,
        alt: "Товари для домашніх заготовок",
      },
    ],
  },
  twitter: {
    card: "summary_large_image",
    title: "Товари для домашніх заготовок",
    description:
      "Корисні категорії товарів для консервації, ферментації та сезонної кухні.",
    images: [absoluteUrl(siteConfig.ogImage, siteConfig.url)],
  },
}

const productGroups = [
  {
    title: "Банки та кришки",
    description:
      "Основа будь-якої домашньої консервації. У цьому розділі зібрані базові товари, без яких складно уявити сезон заготовок: банки різного об’єму, кришки та корисні дрібниці для зберігання.",
    icon: Package,
  },
  {
    title: "Ваги та мірні інструменти",
    description:
      "Особливо корисні для стабільних пропорцій, ферментації, маринадів і великих партій заготовок. Точні вимірювання часто допомагають уникнути прикрих помилок.",
    icon: Scale,
  },
  {
    title: "Кухонні інструменти",
    description:
      "Закаточні ключі, лійки, ножі, дошки, щипці, миски та інші дрібні помічники, які роблять приготування зручнішим і спокійнішим у розпал сезону.",
    icon: ChefHat,
  },
]

const useCases = [
  "підготуватися до першого сезону домашніх заготовок",
  "докупити лише найнеобхідніше без зайвих витрат",
  "зібрати базовий набір для консервації та ферментації",
  "знайти корисні дрібниці для великої сезонної партії",
]

const nextLinks = [
  {
    title: "Рецепти",
    href: "/recipes",
  },
  {
    title: "Сезонні хаби",
    href: "/seasonal",
  },
  {
    title: "Калькулятори",
    href: "/calculators",
  },
]

export default function ProductsPage() {
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Товари для домашніх заготовок",
    description:
      "Корисні категорії товарів для консервації, ферментації й сезонної кухні.",
    url: absoluteUrl("/products", siteConfig.url),
  }

  return (
    <>
      <script
        id="products-webpage-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />

      <section className="border-b border-neutral-200 bg-neutral-50 py-12 sm:py-16">
        <Container>
          <div className="max-w-4xl">
            <div className="eyebrow">Товари для кухні</div>

            <h1 className="mt-4 text-4xl font-semibold leading-tight text-neutral-950 sm:text-5xl">
              Що може знадобитися для домашніх заготовок
            </h1>

            <p className="mt-5 text-base leading-8 text-neutral-700 sm:text-lg">
              У цьому розділі зібрані категорії товарів, які найчастіше
              використовують для консервації, ферментації, варення, компотів і
              сезонної кухні. Це зручна навігація по базовому інвентарю та
              корисних кухонних помічниках, які полегшують домашні заготовки.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container>
          <div className="mb-8 max-w-3xl">
            <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
              Основні категорії
            </h2>

            <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
              Якщо ви тільки збираєте базовий набір для заготовок або хочете
              оновити інвентар перед сезоном, почніть з найпотрібніших
              категорій.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {productGroups.map((item) => {
              const Icon = item.icon

              return (
                <article
                  key={item.title}
                  className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h2 className="text-xl font-semibold text-neutral-950">
                    {item.title}
                  </h2>

                  <p className="mt-3 text-sm leading-7 text-neutral-600">
                    {item.description}
                  </p>
                </article>
              )
            })}
          </div>
        </Container>
      </section>

      <section className="border-y border-neutral-200 bg-neutral-50 py-12 sm:py-14">
        <Container>
          <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr] lg:items-center">
              <div>
                <div className="eyebrow">
                  <ShoppingBasket className="mr-2 h-3.5 w-3.5" />
                  Практичний підхід
                </div>

                <h2 className="mt-4 text-2xl font-semibold leading-tight text-neutral-950 sm:text-3xl">
                  Купувати зручно те, що справді стане в пригоді
                </h2>

                <p className="mt-4 text-sm leading-7 text-neutral-600 sm:text-base">
                  Для домашніх заготовок не завжди потрібна велика кількість
                  спеціального обладнання. Часто достатньо продуманого базового
                  набору: банок, кришок, зручних інструментів і точних мірних
                  помічників.
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5">
                <div className="text-sm font-semibold text-neutral-950">
                  Коли цей розділ особливо корисний
                </div>

                <ul className="mt-3 space-y-3 text-sm leading-7 text-neutral-600">
                  {useCases.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-1 text-emerald-700">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container>
          <div className="mb-8 max-w-3xl">
            <h2 className="text-2xl font-semibold text-neutral-950 sm:text-3xl">
              З чим це найкраще поєднувати
            </h2>

            <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
              Розділ товарів працює сильніше, коли він пов’язаний із рецептами,
              сезонними сторінками та сервісними інструментами.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {nextLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="text-lg font-semibold text-neutral-950">
                    {item.title}
                  </div>
                  <Calculator className="h-5 w-5 text-emerald-700" />
                </div>

                <div className="mt-5 inline-flex items-center text-sm font-semibold text-emerald-700">
                  Перейти
                  <ArrowRight className="ml-2 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 sm:py-14">
        <Container className="grid gap-4 lg:grid-cols-2">
          <KuhlykTip text="Найзручніше починати не з великого списку покупок, а з базового набору. Коли основні інструменти вже під рукою, сезон домашніх заготовок проходить спокійніше й організованіше." />

          <KuhlykTip
            variant="warm"
            title="Порада від Кухлика"
            text="Якщо готуєте часто, особливо корисно мати хороші ваги, зручну лійку для банок і кілька перевірених ємностей для зберігання. Саме ці дрібниці найчастіше економлять час і сили."
          />
        </Container>
      </section>

      <section className="pb-16">
        <Container>
          <div className="rounded-[2rem] border border-neutral-200 bg-gradient-to-br from-white to-emerald-50/50 p-6 shadow-sm sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr] lg:items-center">
              <div>
                <div className="eyebrow">Корисні розділи поруч</div>

                <h2 className="mt-4 text-2xl font-semibold leading-tight text-neutral-950 sm:text-3xl">
                  Обирайте інструменти під конкретний рецепт або сезон
                </h2>

                <p className="mt-4 text-sm leading-7 text-neutral-600 sm:text-base">
                  Найзручніше підбирати товари не абстрактно, а під конкретну
                  задачу: маринування огірків, осінню ферментацію, літні
                  компоти, варення чи підготовку до великої сезонної партії.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link href="/recipes" className="button-primary">
                  Перейти до рецептів
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>

                <Link href="/seasonal" className="button-secondary">
                  Відкрити сезонні хаби
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}